/**
 * Tiny module-level pubsub so MicCheckGate can signal useProctor to back
 * off the shared audio track while the 5-second enrolment capture runs.
 *
 * Why not thread refs/callbacks through RequireProctor's render-prop chain?
 * useProctor + MicCheckGate are rendered three layers apart (RequireProctor
 * → section page → MicCheckGate). A one-direction bus keeps the contract
 * simple:
 *   • MicCheckGate.publish('acquire')  → useProctor stops its slice-recorder
 *     so two MediaRecorders don't contend for the mic hardware (which some
 *     mobile browsers resolve by muting the LATER opener — the exact
 *     "waveform not animating / recording too short" symptom reported in
 *     iter45).
 *   • MicCheckGate.publish('release')  → useProctor is free to resume
 *     capturing audio slices on the next 60-sec interval.
 *
 * Subscribers are plain functions. There's no retained history, no state.
 */
const _subs = new Set();

export const proctorMicBus = {
  subscribe(fn) {
    _subs.add(fn);
    return () => _subs.delete(fn);
  },
  publish(event) {
    _subs.forEach((fn) => {
      try { fn(event); } catch (_) { /* subscriber errors must not take down the bus */ }
    });
  },
};

// Simple flag mirror so a late-firing timer in useProctor can short-circuit
// without needing to subscribe. useProctor also subscribes for the race-safe
// path, but startSlice() reads this flag as a cheap guard.
let _micInUse = false;
export const setMicInUse = (v) => { _micInUse = Boolean(v); };
export const isMicInUse = () => _micInUse;
